/**
 * Escrow deployments: data and actions for the admin pages.
 *
 * The API runs every on-chain check itself. These hooks only send the admin's request and keep
 * the cached list current.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client.js";

/** A registered escrow deployment, as `GET /api/deployments` describes it. */
export interface Deployment {
  chainId: number;
  /** Escrow address in lowercase hex. */
  address: string;
  version: string;
  deploymentBlock: number;
  acceptsNewCampaigns: boolean;
  creationPaused: boolean;
  /** ISO 8601 time of the last successful on-chain check. */
  checkedAt: string;
  registeredAt: string;
  /** The campaign tokens the escrow accepts. */
  tokens: { address: string; symbol: string; decimals: number }[];
}

/** Query key of the deployment list in the React Query cache. */
export const DEPLOYMENTS_QUERY_KEY = ["deployments"] as const;

/** The deployments registered on the platform's chain, newest first. */
export function useDeployments() {
  return useQuery({
    queryKey: DEPLOYMENTS_QUERY_KEY,
    queryFn: async () =>
      (await api.get<{ deployments: Deployment[] }>("/api/deployments")).deployments,
  });
}

/** An admin action on deployments that refreshes the list when it succeeds. */
function useDeploymentAction<Input>(action: (input: Input) => Promise<Deployment>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSettled: () => queryClient.invalidateQueries({ queryKey: DEPLOYMENTS_QUERY_KEY }),
  });
}

/** Registers a deployment. The API refuses it unless every on-chain check passes. */
export function useRegisterDeployment() {
  return useDeploymentAction(
    (input: { address: string; deploymentBlock: number; acceptsNewCampaigns: boolean }) =>
      api.post<Deployment>("/api/admin/deployments", input),
  );
}

/** Checks a deployment against the chain again. */
export function useCheckDeployment() {
  return useDeploymentAction((address: string) =>
    api.post<Deployment>(`/api/admin/deployments/${address}/check`),
  );
}

/** Starts or stops new campaigns on a deployment. */
export function useSetAcceptsNewCampaigns() {
  return useDeploymentAction((input: { address: string; acceptsNewCampaigns: boolean }) =>
    api.patch<Deployment>(`/api/admin/deployments/${input.address}`, {
      acceptsNewCampaigns: input.acceptsNewCampaigns,
    }),
  );
}
